import React, { useEffect, useMemo, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
// @ts-expect-error TODO fix type error
const DatePicker = dynamic(() => import('react-datepicker'), { ssr: true });
import { registerLocale } from 'react-datepicker';
import Tooltip from 'components/Generic/Tooltip';
const currentDate = new Date();
import { useI18n } from 'next-localization';
import debounce from 'lodash.debounce';
import { useDispatch } from 'react-redux';
import useHelpers from 'hooks/helpers';
import 'react-datepicker/dist/react-datepicker.css';
import { fundCentreActions } from '@/store/fundCentre';
import ThaiDatepickerHeader from './ThaiDatepickerHeader';
import ThaiYearInput from './ThaiYearInput';

import { useSitecoreContext } from 'hooks/useSitecoreContext';

const dateValidationRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
interface DatePickerProps {
  inline: boolean;
  showPopper?: boolean;
  toggleFiltersAside?: () => void;
}

const DatepickerFilter = ({
  inline = false,
  showPopper = undefined,
}: DatePickerProps): JSX.Element => {
  const searchParams = new URLSearchParams(window.location.search);
  const date = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [localeLang, setLocaleLang] = useState('en');
  const [error, setError] = useState(false);
  const { sitecoreContext } = useSitecoreContext();
  const languageWithoutRegion = sitecoreContext.language?.split('-')[0];
  const { t } = useI18n();
  const { updateUrlParams } = useHelpers();
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const isThai = sitecoreContext.language?.toLowerCase() === 'th-th';

  const onChange = (selectedDate: Date | null) => {
    if (selectedDate) {
      const date = new Date(selectedDate);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
      const day = String(date.getDate()).padStart(2, '0');

      const formattedDate = `${year}-${month}-${day}`;
      setSelectedDate(selectedDate);
      dispatch(fundCentreActions.setSelectedDate(formattedDate));
      updateUrlParams([{ name: 'date', value: formattedDate }]);
      setError(false);
    } else {
      setSelectedDate(null);
      dispatch(fundCentreActions.setSelectedDate(null));
      updateUrlParams([{ name: 'date', value: '' }]);
    }
  };

  const format = useMemo(() => {
    // Get the date format parts based on the locale
    const formatter = new Intl.DateTimeFormat(sitecoreContext.language);

    const parts = formatter.formatToParts(new Date());

    // Map the parts to a string format (e.g., MMMM-DD-YY or DD-MMMM-YY)
    const formatParts = parts.map((part) => {
      switch (part.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return '/';
      }
    });

    // Join the parts to form the final format string
    return formatParts.join('');
  }, [sitecoreContext.language]);

  const inputDateValidationRegex =
    format === 'dd/MM/yyyy'
      ? /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/
      : /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
  };

  useEffect(() => {
    if (date && dateValidationRegex.test(date)) {
      setSelectedDate(new Date(date));
      dispatch(fundCentreActions.setSelectedDate(date));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    let lang = languageWithoutRegion;
    let locale = { default: null };
    const loadLocale = async () => {
      if (lang === 'zh') {
        lang = 'zh-HK';
      }
      try {
        locale = await import(`node_modules/date-fns/locale/${lang}.js`);
        setLocaleLang(lang || 'en');
      } catch (e) {
        console.error(`Failed to load locale for ${lang}`);
      } finally {
        if (locale.default) {
          registerLocale((locale.default as { code: string }).code, locale.default);
        }
      }
    };
    loadLocale();
  }, []);

  const validateDate = debounce((value: string) => {
    if (value === '' || value === undefined) {
      setError(false);
    } else if (!inputDateValidationRegex.test(value)) {
      setError(true);
    } else {
      setError(false);
    }
  }, 700);

  const handleValidation = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputValue = (e.target as HTMLInputElement).value;
    validateDate(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      const inputElement = wrapperRef.current?.querySelector('input');
      e.preventDefault();
      if (inputElement) {
        inputElement.focus();
      }
    }
  };

  const screenReaderText = selectedDate
    ? t('fundCentreFiltersDateUpdated').replace('{token.date}', selectedDate.toLocaleDateString())
    : `Date format: ${format}, list box pop-up expanded, combo box`;

  useEffect(() => {
    if (error) {
      const wrapper = wrapperRef.current;
      const inputElement = wrapper?.querySelector('input');
      inputElement?.setAttribute('aria-invalid', 'true');
    }
  }, [error]);

  return (
    <div className="flex-column">
      {!inline && (
        <div className="d-flex mb-1.5">
          <label htmlFor="datePicker" className="form-label d-flex">
            {t('fundsFundCentreFiltersPricesDate')}
            {t('fundsFundCentreTooltipDatePicker') &&
              t('fundsFundCentreTooltipDatePicker') !== 'fundsFundCentreTooltipDatePicker' && (
                <Tooltip
                  tooltipContent={t('fundsFundCentreTooltipDatePicker')}
                  className="d-flex align-items-center"
                />
              )}
            <span className="sr-only" aria-live="polite" role="alert" id="custom-date-label">
              {error ? t('fundCentreFiltersDateError') : screenReaderText}
            </span>
          </label>
        </div>
      )}
      <div ref={wrapperRef} onKeyDown={handleKeyDown}>
        <DatePicker
          selected={selectedDate ? new Date(selectedDate) : null}
          placeholderText={error ? '' : t('selectDate')}
          showIcon={!selectedDate}
          inline={inline}
          wrapperClassName={`date-picker__wrapper ${error ? 'date-picker__wrapper--error' : ''}`}
          icon={<i className="bi bi-calendar" />}
          isClearable={!!selectedDate}
          filterDate={isWeekday}
          onInputError={() => setError(true)}
          ariaLabelledBy="custom-date-label"
          id="datePicker"
          onChangeRaw={handleValidation}
          name="datePicker"
          calendarStartDay={1}
          calendarIconClassName="calendar-icon"
          className="date-picker"
          popperClassName={inline ? 'date-picker-popper__inline' : ''}
          dateFormat={format}
          onChange={onChange}
          maxDate={currentDate}
          open={showPopper}
          locale={localeLang}
          customInput={isThai ? <ThaiYearInput /> : undefined}
          renderCustomHeader={
            isThai
              ? (headerProps) => (
                  <ThaiDatepickerHeader
                    {...headerProps}
                    language={sitecoreContext.language || 'en'}
                  />
                )
              : undefined
          }
        />
      </div>
      {error && (
        <p className="mt-1.5 text-error-400 field-error-text order-4">
          {t('fundCentreFiltersDateError')}
        </p>
      )}
      {!inline && (
        <p id="format" className="mt-1.5 mb-1.5 hint-text">
          {t('fundCentreFiltersDateFormat')} {format.toLowerCase()}
        </p>
      )}
    </div>
  );
};

export default DatepickerFilter;
